import { select, mouse } from 'd3-selection';
import 'd3-selection-multi';
import _keys from 'lodash/keys';
import _indexOf from 'lodash/indexOf';
import _last from 'lodash/last';

// Modules
import BubbleGraph from 'modules/BubbleGraph';
import Timeline from 'modules/Timeline';

// Utils
import { guid, toggleButtonPrefix, separateButtonPrefix } from 'utils/chart';
import { regionColors } from 'utils/colors';
import { legendStyle, svgStyle, buttonContainerStyle, buttonStyle, loadingStyle, textButtonStyle, dragSvgStyle } from 'utils/styles';
import Tooltip from 'utils/tooltip';

export default class Graph {
  constructor(element, dragArea) {
    this.element = element;
    this.container = select(element);
    this.container.select('.interaction').attr('class', `interaction ${buttonContainerStyle}`);
    this._createLoadingScreen();
    this.canvas = this.container.append('canvas');
    this.svg = this.container.append('svg').attr('class', svgStyle);
    this.dragSvg = select(dragArea).attr('class', dragSvgStyle);

    this.bubbleGraph = new BubbleGraph(this.canvas);
    this.container.on('mousemove', this._onMouseMove.bind(this));
    this.timeline = new Timeline(this.svg, this.dragSvg);
    this.tooltip = new Tooltip(this.container);
    this.timeline.setOnMarkerClickFunction(this.onMarkerClick.bind(this));
    this.dataRecordIdx = 0;
    this.pixelRatio = window.devicePixelRatio;
    this.animationDelta = 1000;
    this.minWidth = 700;
    this.minHeight = 700;

    this._createLegend();
    this._createToggleButton();
    this._createSeparateButton();

    this.isPlaying = false;
    this.isSeparate = false;

    select(window).on(`resize.chart_${guid()}`, () => this.render(false, true));
  }

  render(firstRender, resize) {
    const { element, svg, canvas, bubbleGraph, pixelRatio, timeline, year, isSeparate, animationDelta, minWidth, minHeight } = this;
    const width = element.clientWidth;
    const height = element.clientHeight;

    const kWidth = minWidth / width;
    const kHeight = minHeight / height;
    const k = kWidth; //Math.max(kWidth, kHeight);
    const svgHeight = height * 0.45;
    const bubbleHeight = height;

    canvas
      .attrs({
        width: width * pixelRatio,
        height: bubbleHeight * pixelRatio,
      })
      .styles({
        width: `${width}px`,
        height: `${bubbleHeight}px`,
      });
    svg.attrs({
      width: width,
      height: svgHeight / (k > 1 ? kWidth : 1),
      viewBox: k > 1 ? `0 0 ${width * kWidth} ${svgHeight}` : null,
    });

    let bubbleK = k;
    if (width <= 1024) {
      bubbleK = width < 820 ? bubbleK * 2 : bubbleK * 1.75;
    }

    bubbleGraph.render({
      isSeparate,
      svgHeight,
      animationDelta,
      pixelRatio: pixelRatio,
      k: bubbleK > 1 ? bubbleK : 1,
      currentYear: year,
      width: width, // * pixelRatio,
      height: bubbleHeight, // * pixelRatio,
    }, firstRender);

    timeline.render({
      animationDelta: resize ? 0 : animationDelta,
      width: k > 1 ? minWidth : width,
      currentYear: _indexOf(this.years, year) !== -1 ? year : _last(this.years),
      height: svgHeight,
    }, firstRender);

    // this.legend.style('transform', `scale(${k > 1 ? 1 / k : 1})`);

    if (firstRender) setTimeout(() => { this._play(); }, 1000);
  }

  _startAnimation() {
    this.timer = setInterval(() => {
      this.dataRecordIdx += 1;
      if (this.years[this.dataRecordIdx] !== undefined) {
        this.year = this.years[this.dataRecordIdx];
        this.render();
      } else {
        this.isPlaying = false;
        this.dataRecordIdx = 0;
        this.playIcon.attr('class', `${buttonStyle('play')}`);
        clearInterval(this.timer);
      }
    }, this.animationDelta);
  }

  onMarkerClick(idx, dragging) {
    if (this.isPlaying && !dragging) {
      this.dataRecordIdx = idx;
    } else {
      this.dataRecordIdx = idx + 1;
      this.year = this.years[this.dataRecordIdx];
      this.isSeparate = false;
      this.render();

      this._updateButtons();
    }
  }

  _play() {
    this.playIcon.attr('class', `${buttonStyle('pause')}`);
    this.isPlaying = true;
    this._startAnimation();
  }

  _pause() {
    this.playIcon.attr('class', `${buttonStyle('play')}`);
    this.isPlaying = false;
    clearInterval(this.timer);
  }

  _createLegend() {
    const { container } = this;
    const regions = {
      US: 'North America',
      EU: 'Europe',
      AS: 'Asia',
      Rest: 'Rest',
    };
    this.legend = container.append('div').attr('class', legendStyle);
    const items = this.legend.selectAll('.item').data(_keys(regions))
      .enter()
      .append('div')
      .attr('class', 'item');

    items
      .append('div')
      .attr('class', 'box')
      .style('background-color', d => regionColors[d]);
    items
      .append('text')
      .text(d => regions[d]);
  }

  _createToggleButton() {
    const { container } = this;
    const playButtonContainer = container.select('.interaction')
      .append('div')
      .attr('class', `${toggleButtonPrefix}-item`);
    this.playIcon = playButtonContainer
      .append('div')
      .attr('class', `${buttonStyle('play')}`)
      .on('click', () => {
        if (this.isPlaying) this._pause();
        else this._play();
      });
    playButtonContainer
      .append('div')
      .attr('class', textButtonStyle)
      .html('Press play to start / restart animation');
  }

  _createSeparateButton() {
    const { container } = this;
    const separateContainer = container.select('.interaction')
      .append('div')
      .attr('class', `${separateButtonPrefix}-item`);

    this.separateIcon = separateContainer
      .append('div')
      .attr('class', `${buttonStyle('separate')}`)
      .on('click', () => {
        this.isSeparate = !this.isSeparate;
        if (this.isSeparate) {
          this._pause();
          this.dataRecordIdx = this.years.length - 1;
          this.year = '2025';
        } else {
          this.year = this.years[this.dataRecordIdx];
        }
        this.render();
        this._updateButtons();

      });
    separateContainer
      .append('div')
      .attr('class', textButtonStyle)
      .html('Separate World-Class in 2025');
  }

  _updateButtons() {
    this.playIcon.styles({
      opacity: this.isSeparate ? 0.5 : 1,
      'pointer-events': this.isSeparate ? 'none' : 'all',
    });
  }

  setData(bubbleData, separateData, timelineData) {
    this.years = _keys(bubbleData[0]).filter(d => +d);
    this.year = this.years[this.dataRecordIdx];
    this.bubbleGraph.setData(bubbleData, separateData);
    this.timeline.setData(timelineData, this.years);
    this._removeLoadingScreen();
  }

  setFont(fontFamily) {
    this.container.selectAll('text, div').style('font-family', fontFamily);
    this.timeline.setFont(fontFamily);
    this.bubbleGraph.setFont(fontFamily);
  }

  _onMouseMove() {
    const { element } = this;
    const node = this.bubbleGraph.findNode(mouse(element));
    if (node) this.tooltip.show(node.name, mouse(element));
    else this.tooltip.hide();
  }

  _createLoadingScreen() {
    this.loadingScreen = this.container
      .append('div')
      .attr('class', loadingStyle);
    this.loadingScreen
      .append('span')
      .html('Loading data ... ');
  }

  _removeLoadingScreen() {
    this.loadingScreen
      .transition()
      .duration(200)
      .style('opacity', 0)
      .remove();
  }
}
